import { useState } from 'react';
import {
  UtensilsCrossed, Search, X, ChefHat, Layers, Box, DollarSign, Sparkles 
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CrudToolbar } from '@/components/ui/crud-toolbar';
import {
  RecipeCard, RecipeDetailModal, RecipeFormModal, GenAIRecipeModal 
} from '@/features/recipes';
import type {
  Recipe, RecipeFormData, GeneratedRecipe 
} from '@/features/recipes';
import { DIETARY_OPTIONS } from './recipe-constants';

interface TypeCounts {
  all: number;
  finished: number;
  'sub-recipe': number;
  component: number;
}

interface LibraryViewProps {
  readonly recipes: Recipe[];
  readonly allRecipes: Recipe[];
  readonly typeCounts: TypeCounts;
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly searchQuery: string;
  readonly setSearchQuery: (query: string) => void;
  readonly selectedDietary: string;
  readonly setSelectedDietary: (dietary: string) => void;
  readonly showAddModal: boolean;
  readonly setShowAddModal: (show: boolean) => void;
  readonly editingRecipe: Recipe | null;
  readonly setEditingRecipe: (recipe: Recipe | null) => void;
  readonly viewingRecipe: Recipe | null;
  readonly setViewingRecipe: (recipe: Recipe | null) => void;
  readonly onSaveRecipe: (data: RecipeFormData) => void;
  readonly onBulkUpload: (file: File) => void;
  readonly onRefetch: () => void;
  readonly activeTab: string;
}

export function LibraryView({
  recipes,
  allRecipes,
  typeCounts,
  isLoading,
  error,
  searchQuery,
  setSearchQuery,
  selectedDietary,
  setSelectedDietary,
  showAddModal,
  setShowAddModal,
  editingRecipe,
  setEditingRecipe,
  viewingRecipe,
  setViewingRecipe,
  onSaveRecipe,
  onBulkUpload,
  onRefetch,
  activeTab,
}: LibraryViewProps) {
  const [showGenAIModal, setShowGenAIModal] = useState(false);

  const handleAddRecipe = () => {
    setEditingRecipe(null);
    setShowAddModal(true);
  };

  const handleEditRecipe = (recipe: Recipe) => {
    setViewingRecipe(null);
    setEditingRecipe(recipe);
    setShowAddModal(true);
  };

  const handleGenAIRecipe = (generatedRecipe: GeneratedRecipe) => {
    // Convert GeneratedRecipe to RecipeFormData format and open the form modal
    const formData: RecipeFormData = {
      name: generatedRecipe.name,
      description: generatedRecipe.description,
      type: generatedRecipe.type,
      category: generatedRecipe.category,
      cuisine: generatedRecipe.cuisine ?? '',
      servings: generatedRecipe.servings,
      servingSize: generatedRecipe.servingSize ?? '',
      prepTime: generatedRecipe.prepTime,
      cookTime: generatedRecipe.cookTime,
      yield: generatedRecipe.yield,
      yieldUnit: generatedRecipe.yieldUnit,
      difficulty: generatedRecipe.difficulty,
      ingredients: generatedRecipe.ingredients.map((ing, idx) => ({
        ingredientId: `gen-${idx}`,
        itemId: `gen-item-${idx}`,
        itemName: ing.itemName,
        quantity: ing.quantity,
        unit: ing.unit,
        prepNotes: ing.prepNotes,
        isOptional: false,
      })),
      subRecipeIds: [],
      instructions: generatedRecipe.instructions,
      allergens: generatedRecipe.allergens,
      dietaryFlags: generatedRecipe.dietaryFlags,
    };
    
    // Save the generated recipe
    onSaveRecipe(formData);
  };

  const getDefaultRecipeType = (): 'finished' | 'sub-recipe' | 'component' => {
    if (activeTab === 'menu-items') return 'finished';
    if (activeTab === 'sub-recipes') return 'sub-recipe';
    if (activeTab === 'components') return 'component';
    return 'finished';
  };

  const exportData = recipes.map((r) => ({
    name: r.name,
    type: r.type,
    category: r.category,
    prepTime: r.prepTime ?? '',
    cookTime: r.cookTime ?? '',
    yield: r.yield ?? '',
    yieldUnit: r.yieldUnit ?? '',
    costPerPortion: r.costPerPortion ?? '',
  }));

  return (
    <>
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={() => setShowGenAIModal(true)} variant="outline" className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-purple-200 hover:border-purple-300">
          <Sparkles className="mr-2 h-4 w-4 text-purple-500" />
          Create with AI
        </Button>
        <CrudToolbar
          entityName="Recipe"
          entityNamePlural="Recipes"
          selectedCount={0}
          onAdd={handleAddRecipe}
          onBulkUpload={onBulkUpload}
          exportData={exportData}
          exportFilename="recipes.csv"
        />
      </div>

      <GenAIRecipeModal
        isOpen={showGenAIModal}
        onClose={() => setShowGenAIModal(false)}
        onRecipeGenerated={handleGenAIRecipe}
        defaultType={getDefaultRecipeType()}
      />

      <RecipeFormModal
        recipe={editingRecipe}
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setEditingRecipe(null);
        }}
        onSave={onSaveRecipe}
        allRecipes={allRecipes}
      />

      <RecipeDetailModal
        recipe={viewingRecipe}
        isOpen={viewingRecipe !== null}
        onClose={() => setViewingRecipe(null)}
        allRecipes={allRecipes}
        onNavigateToRecipe={(recipe) => setViewingRecipe(recipe)}
        onEdit={handleEditRecipe}
      />

      <SummaryCards typeCounts={typeCounts} activeTab={activeTab} />

      <SearchAndFilters
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        selectedDietary={selectedDietary}
        setSelectedDietary={setSelectedDietary}
      />

      <RecipeGrid
        recipes={recipes}
        isLoading={isLoading}
        error={error}
        searchQuery={searchQuery}
        selectedDietary={selectedDietary}
        onViewRecipe={setViewingRecipe}
        onRefetch={onRefetch}
      />
    </>
  );
}

function SummaryCards({
  typeCounts, activeTab 
}: {
  readonly typeCounts: TypeCounts;
  readonly activeTab: string 
}) {
  return (
    <div className="grid gap-4 md:grid-cols-5">
      <Card className={activeTab === 'menu-items' ? 'ring-2 ring-primary' : ''}>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Menu Items</CardTitle>
          <ChefHat className="h-5 w-5 text-blue-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-blue-600">{typeCounts.finished}</div>
        </CardContent>
      </Card>
      <Card className={activeTab === 'sub-recipes' ? 'ring-2 ring-primary' : ''}>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Sub-Recipes</CardTitle>
          <Layers className="h-5 w-5 text-purple-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-purple-600">{typeCounts['sub-recipe']}</div>
        </CardContent>
      </Card>
      <Card className={activeTab === 'components' ? 'ring-2 ring-primary' : ''}>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Components</CardTitle>
          <Box className="h-5 w-5 text-gray-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-gray-600">{typeCounts.component}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Total</CardTitle>
          <UtensilsCrossed className="h-5 w-5 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{typeCounts.all}</div>
        </CardContent>
      </Card>
      <Card className={activeTab === 'costing' ? 'ring-2 ring-primary' : ''}>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Avg Cost</CardTitle>
          <DollarSign className="h-5 w-5 text-green-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-green-600">$4.25</div>
        </CardContent>
      </Card>
    </div>
  );
}

interface SearchAndFiltersProps {
  readonly searchQuery: string;
  readonly setSearchQuery: (query: string) => void;
  readonly selectedDietary: string;
  readonly setSelectedDietary: (dietary: string) => void;
}

function SearchAndFilters({
  searchQuery, setSearchQuery, selectedDietary, setSelectedDietary 
}: SearchAndFiltersProps) {
  return (
    <div className="flex flex-col gap-4 md:flex-row md:items-center">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search recipes..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full rounded-md border bg-background py-2 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <select
        value={selectedDietary}
        onChange={(e) => setSelectedDietary(e.target.value)}
        className="rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
      >
        {DIETARY_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </div>
  );
}

interface RecipeGridProps {
  readonly recipes: Recipe[];
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly searchQuery: string;
  readonly selectedDietary: string;
  readonly onViewRecipe: (recipe: Recipe) => void;
  readonly onRefetch: () => void;
}

function RecipeGrid({
  recipes,
  isLoading,
  error,
  searchQuery,
  selectedDietary,
  onViewRecipe,
  onRefetch,
}: RecipeGridProps) {
  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={`skeleton-${i.toString()}`} className="h-64 animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <UtensilsCrossed className="mb-4 h-12 w-12 text-red-500" />
          <p className="text-lg font-medium">Failed to load recipes</p>
          <p className="text-sm text-muted-foreground">
            {error instanceof Error ? error.message : 'An error occurred'}
          </p>
          <Button variant="outline" className="mt-4" onClick={onRefetch}>Try Again</Button>
        </CardContent>
      </Card>
    );
  }

  if (recipes.length === 0) {
    const hasFilters = Boolean(searchQuery) || Boolean(selectedDietary);
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <UtensilsCrossed className="mb-4 h-12 w-12 text-muted-foreground" />
          <p className="text-lg font-medium">No recipes found</p>
          <p className="text-sm text-muted-foreground">
            {hasFilters ? 'Try adjusting your filters' : 'Add recipes to your library to get started'}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {recipes.map((recipe) => (
        <RecipeCard key={recipe.recipeId} recipe={recipe} onClick={() => onViewRecipe(recipe)} />
      ))}
    </div>
  );
}
